import { executeFetch, filteredBrowserHeaders, type Fetcher } from "../FetchSupport.ts";
import { assertSourceUrl } from "../SourceRegistry.ts";
import { assertVideoDownloadUrl } from "../AnimeMediaUrl.ts";
import type { HttpRequest, ImageRequest, PreparedImage } from "../types.ts";

export class DirectTransport {
  constructor(private readonly fetcher: Fetcher = (input, init) => fetch(input, init)) {}

  text(request: HttpRequest & { responseType: "text" }) {
    return this.execute(request, (bytes) => new TextDecoder().decode(bytes));
  }

  bytes(request: HttpRequest & { responseType: "bytes" }) {
    return this.execute(request, (bytes) => bytes);
  }

  async prepareImage(request: ImageRequest): Promise<PreparedImage> {
    assertSourceUrl(request.sourceId, request.url);
    return { url: request.url, delivery: "direct", release() {} };
  }

  private execute<T extends string | Uint8Array>(
    request: HttpRequest,
    decode: (bytes: Uint8Array) => T,
  ) {
    const url = assertSourceUrl(request.sourceId, request.url);
    return executeFetch(
      request,
      "direct",
      this.fetcher,
      url.href,
      {
        method: request.method,
        headers: filteredBrowserHeaders(request.headers),
        body: request.body,
        credentials: request.credentials,
        mode: "cors",
      },
      decode,
      undefined,
      (response) => {
        const target = response.url || url.href;
        assertSourceUrl(request.sourceId, target);
        return target;
      },
    );
  }
}

/** Video downloads stream straight to disk, without Worker traffic or a full RAM copy. */
export async function streamVideoToDisk(url: string, writable: FileSystemWritableFileStream, signal: AbortSignal,
  progress: (bytes: number, total: number) => void,
  fetcher: Fetcher = (input, init) => fetch(input, init)): Promise<number> {
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const target = assertVideoDownloadUrl(url).href;
    const response = await fetcher(target, { signal, credentials: 'omit', mode: 'cors' });
    assertVideoDownloadUrl(response.url || target);
    if (!response.ok || !response.body) throw new Error(`El servidor no permite descargar el video (HTTP ${response.status}).`);
    const type = response.headers.get('content-type') || '';
    if (!/video\/|application\/octet-stream/i.test(type)) throw new Error('El servidor no devolvió un archivo de video descargable.');
    const total = Number(response.headers.get('content-length')) || 0;
    reader = response.body.getReader();
    let bytes = 0;
    for (;;) {
      signal.throwIfAborted();
      const result = await reader.read();
      if (result.done) break;
      await writable.write(result.value as Uint8Array<ArrayBuffer>);
      bytes += result.value.byteLength; progress(bytes, total);
    }
    if (!bytes) throw new Error('El archivo de video está vacío.');
    await writable.close();
    return bytes;
  } catch (error) {
    await reader?.cancel().catch(() => {});
    await writable.abort().catch(() => {});
    throw error;
  } finally { reader?.releaseLock(); }
}
